import React, { useMemo, useState } from "react"
import { motion } from "framer-motion"
import Dialog from "../../Components/Dialog"
import InlineTextField from "../../Components/InLineTextField"
import Button from "../../Components/Button"
import API from "../../Common/API"

const normalizePhoneDigits = (value) => (value || "").toString().replace(/\D/g, "")

const calculateAge = (birthdate) => {
    if (!birthdate) return "-"
    const today = new Date()
    const birthDate = new Date(birthdate)
    if (Number.isNaN(birthDate.getTime())) return "-"

    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1
    }

    return age
}

const getOppositeProfileType = (profileType) => {
    if (profileType === "bride") return "groom"
    if (profileType === "groom") return "bride"
    return ""
}

const buildProfileSummary = (profile, index) => {
    const fullName = [profile?.firstName, profile?.surname].filter(Boolean).join(" ").trim()
    const age = calculateAge(profile?.dateOfBirth)
    const profession = profile?.occupationDetail || profile?.occupation || "-"
    const location = [profile?.occupationLocation, profile?.contactInformation?.presentAddress?.city].filter(Boolean).join(", ") || "-"

    return `${index + 1}. ${fullName || "Profile"} | Ref: ${profile?.refId || "-"} | Age: ${age} | Profession: ${profession} | Location: ${location}`
}

const WhatsAppMatchShareDialog = ({
    open,
    onClose,
    targetUser,
    profiles = [],
}) => {
    const [filter, setFilter] = useState("")
    const [selectedIds, setSelectedIds] = useState([])
    const [approvedProfiles, setApprovedProfiles] = useState([])

    const targetPhone = targetUser?.contactInformation?.whatsappNumber || targetUser?.contactInformation?.mobileNumber || ""
    const cleanTargetPhone = normalizePhoneDigits(targetPhone)
    const preferredType = getOppositeProfileType(targetUser?.profileType)

    React.useEffect(() => {
        if (!open) return

        API.get("/user/getUserListByStatus/1").then((response) => {
            if (response.status === 200) {
                setApprovedProfiles(response?.data?.data?.users || [])
            }
        }).catch(() => {
            setApprovedProfiles([])
        })
    }, [open])

    const eligibleProfiles = useMemo(() => {
        const dedupedProfiles = [...profiles, ...approvedProfiles].reduce((acc, profile) => {
            if (!profile?._id || acc.some((item) => item._id === profile._id)) return acc
            acc.push(profile)
            return acc
        }, [])

        return dedupedProfiles
            .filter((profile) => profile?._id && profile._id !== targetUser?._id)
            .filter((profile) => profile?.activeStat === "A")
            .filter((profile) => profile?.status === 1)
            .filter((profile) => !preferredType || profile?.profileType === preferredType)
            .map((profile) => ({
                ...profile,
                __search: [
                    profile?.refId,
                    profile?.firstName,
                    profile?.surname,
                    profile?.lastName,
                    profile?.occupation,
                    profile?.occupationDetail,
                    profile?.occupationLocation,
                    profile?.contactInformation?.mobileNumber,
                    profile?.contactInformation?.presentAddress?.city,
                ].filter(Boolean).join(" ").toLowerCase(),
            }))
    }, [approvedProfiles, preferredType, profiles, targetUser?._id])

    const filteredProfiles = useMemo(() => {
        const keywords = filter.toLowerCase().trim().split(" ").filter(Boolean)
        if (keywords.length === 0) return eligibleProfiles

        return eligibleProfiles.filter((profile) =>
            keywords.every((keyword) => profile.__search.includes(keyword))
        )
    }, [eligibleProfiles, filter])

    const selectedProfiles = useMemo(() => {
        const selectedSet = new Set(selectedIds)
        return eligibleProfiles.filter((profile) => selectedSet.has(profile._id))
    }, [eligibleProfiles, selectedIds])

    const toggleProfile = (profileId) => {
        setSelectedIds((current) =>
            current.includes(profileId)
                ? current.filter((id) => id !== profileId)
                : [...current, profileId]
        )
    }

    const resetAndClose = () => {
        setFilter("")
        setSelectedIds([])
        if (typeof onClose === "function") onClose()
    }

    const sendMatches = () => {
        if (!cleanTargetPhone || selectedProfiles.length === 0) return

        const formattedNumber = cleanTargetPhone.length === 10 ? `91${cleanTargetPhone}` : cleanTargetPhone
        const greetingName = [targetUser?.firstName, targetUser?.surname].filter(Boolean).join(" ").trim() || "Member"
        const message = [
            `Hello ${greetingName},`,
            "",
            "Here are some matching profiles from Royal Kavery Matrimony:",
            "",
            ...selectedProfiles.map((profile, index) => buildProfileSummary(profile, index)),
            "",
            "Please review these profiles and let us know which ones you would like to proceed with.",
            "",
            "Regards,",
            "Royal Kavery Admin",
        ].join("\n")

        window.open(`https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer")
        resetAndClose()
    }

    return (
        <Dialog showDialog={open} onClose={resetAndClose} title="Send Matching Profiles" injectClass="max-w-5xl">
            <motion.div className="grid gap-5">
                <div className="grid gap-4 rounded-2xl border border-borderColor bg-slate-50 p-5 md:grid-cols-[1.4fr_1fr]">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">Recipient</p>
                        <h4 className="mt-2 text-xl font-semibold text-tColor">
                            {[targetUser?.firstName, targetUser?.surname].filter(Boolean).join(" ").trim() || "Selected user"}
                        </h4>
                        <p className="mt-2 text-sm text-tpColor">
                            WhatsApp: {targetPhone || "Not available"} {preferredType ? `• Showing ${preferredType} profiles` : ""}
                        </p>
                    </div>
                    <div className="flex flex-col justify-between gap-3">
                        <InlineTextField
                            label="Profiles"
                            placeholder="Search matching profiles"
                            value={filter}
                            setValue={setFilter}
                        />
                        <div className="rounded-2xl border border-borderColor bg-white px-4 py-3 text-sm text-tColor">
                            {selectedProfiles.length} profile{selectedProfiles.length === 1 ? "" : "s"} selected
                        </div>
                    </div>
                </div>

                <div className="grid max-h-[420px] gap-3 overflow-y-auto pr-1">
                    {filteredProfiles.map((profile) => {
                        const checked = selectedIds.includes(profile._id)
                        return (
                            <label
                                key={profile._id}
                                className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-4 transition ${
                                    checked
                                        ? "border-primeColor bg-primeSoft/70"
                                        : "border-borderColor bg-white hover:border-primeColor/30 hover:bg-slate-50"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 accent-[#E74C3C]"
                                    checked={checked}
                                    onChange={() => toggleProfile(profile._id)}
                                />
                                <div className="grid gap-1">
                                    <p className="text-base font-semibold text-tColor">
                                        {[profile.firstName, profile.surname].filter(Boolean).join(" ").trim() || "Profile"} {profile.refId ? `• ${profile.refId}` : ""}
                                    </p>
                                    <p className="text-sm text-tpColor">
                                        Age {calculateAge(profile.dateOfBirth)} • {profile.occupationDetail || profile.occupation || "-"} • {[profile.occupationLocation, profile?.contactInformation?.presentAddress?.city].filter(Boolean).join(", ") || "-"}
                                    </p>
                                </div>
                            </label>
                        )
                    })}
                    {filteredProfiles.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-borderColor bg-slate-50 px-4 py-6 text-sm text-tpColor">
                            No matching profiles found for the current search.
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap justify-end gap-3 border-t border-borderColor pt-4">
                    <Button type="secondary" injectClass="rounded-full px-5" onClick={resetAndClose}>Cancel</Button>
                    <Button
                        type="primary"
                        injectClass="rounded-full px-5"
                        onClick={sendMatches}
                        disabled={!cleanTargetPhone || selectedProfiles.length === 0}
                    >
                        Send on WhatsApp
                    </Button>
                </div>
            </motion.div>
        </Dialog>
    )
}

export default WhatsAppMatchShareDialog
